import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';

// Ensure REACT_APP_API_BASE_URL is defined in your .env file
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TrainingForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitStatusType, setSubmitStatusType] = useState(null);
  const [errors, setErrors] = useState({});

  // State to manage visibility of "Other" input fields for various sections
  const [showOtherOccupation, setShowOtherOccupation] = useState(false);
  const [showInvestmentDetails, setShowInvestmentDetails] = useState(false); // For "If yes, what type of programs..."
  const [showOtherGoal, setShowOtherGoal] = useState(false);
  const [showOtherProgram, setShowOtherProgram] = useState(false);
  const [showOtherHearAboutUs, setShowOtherHearAboutUs] = useState(false);


  const boxStyle = {
    flex: 1,
    // minWidth: '500px', // Matches your updated minWidth
    padding: '30px',   // Matches your updated padding
    border: '1px solid violet',
    backgroundColor: 'rgba(238, 130, 238, 0.1)',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };

  // Styles for input/textarea/select fields
  const inputFieldStyle = {
    backgroundColor: 'transparent',
    fontSize: '1.6rem', // Matches your updated font size
    border: '1px solid violet',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);
    setSubmitStatusType(null);
    setErrors({});

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Handle multiple selections for checkboxes
    data.current_occupation = formData.getAll('current_occupation').join(', ');
    data.biggest_goal = formData.getAll('biggest_goal').join(', ');
    data.program_applying_for = formData.getAll('program_applying_for').join(', ');
    data.how_did_you_hear = formData.getAll('how_did_you_hear').join(', ');

    // Logic to merge "Other" text input with the main checkbox/radio field
    const mergeOtherField = (fieldName, otherFieldName) => {
      if (data[fieldName] && data[fieldName].includes('Other') && data[otherFieldName]) {
        data[fieldName] = data[fieldName].replace('Other', `Other (${data[otherFieldName]})`);
      }
      delete data[otherFieldName];
    };

    mergeOtherField('current_occupation', 'current_occupation_other');
    mergeOtherField('biggest_goal', 'biggest_goal_other');
    mergeOtherField('program_applying_for', 'program_applying_for_other');
    mergeOtherField('how_did_you_hear', 'how_did_you_hear_other');

    try {
      const response = await fetch(`${API_BASE_URL}/training-request`, { // Placeholder API endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          setErrors(result.errors);
          setSubmitStatus('Please fix the errors below and try again.');
          setSubmitStatusType('danger');
        } else {
          setSubmitStatus(result.message || 'Submission failed.');
          setSubmitStatusType('danger');
        }
      } else {
        setSubmitStatus(result.message || 'Training application submitted successfully!');
        setSubmitStatusType('success');
        setErrors({});
        e.target.reset(); // Clear form on successful submission
        // Reset "Other" field visibility states after successful submission
        setShowOtherOccupation(false);
        setShowInvestmentDetails(false);
        setShowOtherGoal(false);
        setShowOtherProgram(false);
        setShowOtherHearAboutUs(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('Network or server error. Please try again.');
      setSubmitStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const hasError = (field) => errors && errors[field] && errors[field].length > 0;

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <div className="p-4">
            <h2 className="text-center mb-4">PWU Training Program Application Form</h2>
            <p className="text-center mb-5">Please complete this form so we can determine how best to help you succeed in life, business, and income.</p>

            <Form className="px-5" style={boxStyle} onSubmit={handleSubmit} noValidate>

              {/* --- Section 1: Personal & Contact Information --- */}
              <h3 className="mb-3">Section 1: Personal & Contact Information</h3>

              <Form.Group className="mb-3" controlId="full_name">
                <Form.Label>Full Name (পুরো নাম)*</Form.Label>
                <Form.Control
                  type="text"
                  name="full_name"
                  required
                  style={inputFieldStyle}
                  isInvalid={hasError('full_name')}
                />
                <Form.Control.Feedback type="invalid">{hasError('full_name') && errors.full_name[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="email_address">
                <Form.Label>Email Address (ইমেইল ঠিকানা)*</Form.Label>
                <Form.Control
                  type="email"
                  name="email_address"
                  required
                  style={inputFieldStyle}
                  isInvalid={hasError('email_address')}
                />
                <Form.Control.Feedback type="invalid">{hasError('email_address') && errors.email_address[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="phone_number">
                <Form.Label>Phone Number (ফোন নাম্বার)*</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone_number"
                  required
                  style={inputFieldStyle}
                  isInvalid={hasError('phone_number')}
                />
                <Form.Control.Feedback type="invalid">{hasError('phone_number') && errors.phone_number[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="whatsapp_number">
                <Form.Label>WhatsApp Number (হোয়াটসঅ্যাপ নাম্বার)*</Form.Label>
                <Form.Control
                  type="tel"
                  name="whatsapp_number"
                  required
                  style={inputFieldStyle}
                  isInvalid={hasError('whatsapp_number')}
                />
                <Form.Control.Feedback type="invalid">{hasError('whatsapp_number') && errors.whatsapp_number[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="city_country">
                <Form.Label>City & Country (শহর ও দেশ)*</Form.Label>
                <Form.Control
                  type="text"
                  name="city_country"
                  required
                  style={inputFieldStyle}
                  isInvalid={hasError('city_country')}
                />
                <Form.Control.Feedback type="invalid">{hasError('city_country') && errors.city_country[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="age">
                <Form.Label>Age (বয়স)*</Form.Label>
                <Form.Control
                  type="number"
                  name="age"
                  required
                  min="18"
                  style={inputFieldStyle}
                  isInvalid={hasError('age')}
                />
                <Form.Control.Feedback type="invalid">{hasError('age') && errors.age[0]}</Form.Control.Feedback>
              </Form.Group>

              {/* --- Section 2: Business/Career Information --- */}
              <h3 className="mb-3">Section 2: Business/Career Information</h3>

              <Form.Group className="mb-4">
                <Form.Label>Current Occupation: (বর্তমান পেশা:)*</Form.Label>
                {["Student", "Job Holder", "Entrepreneur", "Freelancer", "Business Owner", "Other"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    label={option}
                    id={`occupation-${option}`}
                    name="current_occupation"
                    value={option}
                    isInvalid={hasError('current_occupation')}
                    onChange={(e) => {
                      if (option === "Other") {
                        setShowOtherOccupation(e.target.checked);
                      }
                    }}
                  />
                ))}
                {showOtherOccupation && (
                  <Form.Control
                    type="text"
                    name="current_occupation_other"
                    placeholder="Please specify"
                    className="mt-2"
                    style={inputFieldStyle}
                  />
                )}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('current_occupation') && errors.current_occupation[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="current_work_description">
                <Form.Label>Describe your current work or business (industry, size, experience): (আপনার বর্তমান কাজ বা ব্যবসা বর্ণনা করুন): *</Form.Label>
                <Form.Control
                  as="textarea"
                  name="current_work_description"
                  required
                  rows={3}
                  style={inputFieldStyle}
                  isInvalid={hasError('current_work_description')}
                />
                <Form.Control.Feedback type="invalid">{hasError('current_work_description') && errors.current_work_description[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="current_stage_revenue_structure">
                <Form.Label>Your current stage, revenue, and structure if any. (আপনার বর্তমান পর্যায়, রাজস্ব এবং কাঠামো যদি থাকে।)*</Form.Label>
                <Form.Control
                  as="textarea"
                  name="current_stage_revenue_structure"
                  required
                  rows={3}
                  style={inputFieldStyle}
                  isInvalid={hasError('current_stage_revenue_structure')}
                />
                <Form.Control.Feedback type="invalid">{hasError('current_stage_revenue_structure') && errors.current_stage_revenue_structure[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>What’s your average monthly income right now? (আপনার গড় মাসিক আয় এখন কি?)*</Form.Label>
                {["Below 50,000 BDT", "50,000 – 1,00,000 BDT", "1,00,000 – 2,00,000 BDT", "2,00,000 – 10,00,000 BDT", "Above 10,00,000 BDT"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="radio"
                    label={option}
                    id={`income-${option}`}
                    name="average_monthly_income"
                    value={option}
                    required
                    isInvalid={hasError('average_monthly_income')}
                  />
                ))}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('average_monthly_income') && errors.average_monthly_income[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Have you invested in personal or business development before? (আপনি কি আগে ব্যক্তিগত বা ব্যবসায়িক উন্নয়নে বিনিয়োগ করেছেন?)*</Form.Label>
                {["Yes", "No"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="radio"
                    label={option}
                    id={`invested-${option}`}
                    name="invested_in_development"
                    value={option}
                    required
                    isInvalid={hasError('invested_in_development')}
                    onChange={(e) => setShowInvestmentDetails(e.target.value === "Yes")}
                  />
                ))}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('invested_in_development') && errors.invested_in_development[0]}</Form.Control.Feedback>
              </Form.Group>

              {showInvestmentDetails && (
                <Form.Group className="mb-3" controlId="investment_details">
                  <Form.Label>If yes, what type of programs/courses/events/coaches have you taken? (যদি হ্যাঁ, আপনি কোন ধরনের প্রোগ্রাম/কোর্স/ইভেন্ট/কোচ নিয়েছেন?)</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="investment_details"
                    rows={3}
                    style={inputFieldStyle}
                    isInvalid={hasError('investment_details')}
                  />
                  <Form.Control.Feedback type="invalid">{hasError('investment_details') && errors.investment_details[0]}</Form.Control.Feedback>
                </Form.Group>
              )}

              {/* --- Section 3: Goals & Challenges --- */}
              <h3 className="mb-3">Section 3: Goals & Challenges</h3>

              <Form.Group className="mb-4">
                <Form.Label>What is your biggest goal in the next 12 months? (আপনার আগামী ১২ মাসের সবচেয়ে বড় লক্ষ্য কি?)*</Form.Label>
                {["Increase income", "Start a business", "Grow/scale my business", "Build financial freedom", "Other"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    label={option}
                    id={`goal-${option}`}
                    name="biggest_goal"
                    value={option}
                    isInvalid={hasError('biggest_goal')}
                    onChange={(e) => {
                      if (option === "Other") {
                        setShowOtherGoal(e.target.checked);
                      }
                    }}
                  />
                ))}
                {showOtherGoal && (
                  <Form.Control
                    type="text"
                    name="biggest_goal_other"
                    placeholder="Please specify"
                    className="mt-2"
                    style={inputFieldStyle}
                  />
                )}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('biggest_goal') && errors.biggest_goal[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="top_challenges">
                <Form.Label>What are your top 3 challenges right now in achieving that goal? (আপনার সেই লক্ষ্য অর্জনে বর্তমানে আপনার শীর্ষ ৩টি চ্যালেঞ্জ কি?)*</Form.Label>
                <Form.Text className="d-block mb-2">(money, mindset, strategy, marketing, sales, structure, clarity, time, etc.)</Form.Text>
                <Form.Control
                  as="textarea"
                  name="top_challenges"
                  required
                  rows={3}
                  style={inputFieldStyle}
                  isInvalid={hasError('top_challenges')}
                />
                <Form.Control.Feedback type="invalid">{hasError('top_challenges') && errors.top_challenges[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Which program are you applying for? (আপনি কোন প্রোগ্রামের জন্য আবেদন করছেন?)*</Form.Label>
                {["Audit Your Life, Business & Income", "Launch Code", "Growth Code", "Scale Code", "Wealth Code", "Sales Code", "Digital Code", "Investment Code", "Career Code", "Other"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    label={option}
                    id={`program-${option}`}
                    name="program_applying_for"
                    value={option}
                    isInvalid={hasError('program_applying_for')}
                    onChange={(e) => {
                      if (option === "Other") {
                        setShowOtherProgram(e.target.checked);
                      }
                    }}
                  />
                ))}
                {showOtherProgram && (
                  <Form.Control
                    type="text"
                    name="program_applying_for_other"
                    placeholder="Please specify"
                    className="mt-2"
                    style={inputFieldStyle}
                  />
                )}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('program_applying_for') && errors.program_applying_for[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="expected_result">
                <Form.Label>What specific result are you expecting from this program? (আপনি এই প্রোগ্রাম থেকে কি নির্দিষ্ট ফলাফল আশা করছেন?)*</Form.Label>
                <Form.Control
                  as="textarea"
                  name="expected_result"
                  required
                  rows={3}
                  style={inputFieldStyle}
                  isInvalid={hasError('expected_result')}
                />
                <Form.Control.Feedback type="invalid">{hasError('expected_result') && errors.expected_result[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="why_good_fit">
                <Form.Label>Why do you think you’re a good fit for this program? (আপনি কেন মনে করেন আপনি এই প্রোগ্রামের জন্য উপযুক্ত?)*</Form.Label>
                <Form.Control
                  as="textarea"
                  name="why_good_fit"
                  required
                  rows={3}
                  style={inputFieldStyle}
                  isInvalid={hasError('why_good_fit')}
                />
                <Form.Control.Feedback type="invalid">{hasError('why_good_fit') && errors.why_good_fit[0]}</Form.Control.Feedback>
              </Form.Group>

              {/* --- Section 4: Commitment & Readiness --- */}
              <h3 className="mb-3">Section 4: Commitment & Readiness</h3>

              <Form.Group className="mb-4">
                <Form.Label>How committed are you to solving your challenges and achieving your goal? (আপনার চ্যালেঞ্জগুলি সমাধান এবং আপনার লক্ষ্য অর্জনে আপনি কতটা প্রতিশ্রুতিবদ্ধ?)*</Form.Label>
                {["Just exploring", "Somewhat committed", "Very committed", "I’ll do whatever it takes"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="radio"
                    label={option}
                    id={`commitment-${option}`}
                    name="commitment_level"
                    value={option}
                    required
                    isInvalid={hasError('commitment_level')}
                  />
                ))}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('commitment_level') && errors.commitment_level[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Are you ready to invest financially in your growth if you qualify? (আপনি কি যদি যোগ্য হন তবে আপনার উন্নয়নে আর্থিকভাবে বিনিয়োগ করতে প্রস্তুত?)*</Form.Label>
                {["No", "Yes, I’m ready and serious"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="radio"
                    label={option}
                    id={`ready-invest-${option}`}
                    name="ready_to_invest"
                    value={option}
                    required
                    isInvalid={hasError('ready_to_invest')}
                  />
                ))}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('ready_to_invest') && errors.ready_to_invest[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>What is your expected budget range for this kind of training/program? (আপনার এই ধরনের প্রশিক্ষণ/প্রোগ্রামের জন্য প্রত্যাশিত বাজেট পরিসীমা কি?)*</Form.Label>
                {["Less than 25,000 BDT", "25,000 – 1,00,000 BDT", "1,00,000 – 5,00,000 BDT", "5,00,000- 10,00000 BDT", "10,00000+ BDT"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="radio"
                    label={option}
                    id={`budget-${option}`}
                    name="expected_budget"
                    value={option}
                    required
                    isInvalid={hasError('expected_budget')}
                  />
                ))}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('expected_budget') && errors.expected_budget[0]}</Form.Control.Feedback>
              </Form.Group>

              {/* --- Section 5: Final --- */}
              <h3 className="mb-3">Section 5: Final</h3>

              <Form.Group className="mb-4">
                <Form.Label>How did you hear about us? (আপনি আমাদের সম্পর্কে কীভাবে জানলেন?)*</Form.Label>
                {["Facebook", "YouTube", "Website", "Friend/Referral", "Event", "Other"].map((option, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    label={option}
                    id={`hear-about-us-${option}`}
                    name="how_did_you_hear"
                    value={option}
                    isInvalid={hasError('how_did_you_hear')}
                    onChange={(e) => {
                      if (option === "Other") {
                        setShowOtherHearAboutUs(e.target.checked);
                      }
                    }}
                  />
                ))}
                {showOtherHearAboutUs && (
                  <Form.Control
                    type="text"
                    name="how_did_you_hear_other"
                    placeholder="Please specify"
                    className="mt-2"
                    style={inputFieldStyle}
                  />
                )}
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{hasError('how_did_you_hear') && errors.how_did_you_hear[0]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="additional_info">
                <Form.Label>Any question or anything else you'd like us to know? (আপনার কি কোন প্রশ্ন আছে বা আমাদের জানাতে চান এমন কিছু?)</Form.Label>
                <Form.Control
                  as="textarea"
                  name="additional_info"
                  rows={3}
                  style={inputFieldStyle}
                  isInvalid={hasError('additional_info')}
                />
                <Form.Control.Feedback type="invalid">{hasError('additional_info') && errors.additional_info[0]}</Form.Control.Feedback>
              </Form.Group>

              <div className="text-center mt-5 mb-5">
                <Button className="btn-default" variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{' '}
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
                {submitStatus && (
              <Alert variant={submitStatusType} className="mt-4">
                {submitStatus}
              </Alert>
            )}
              </div>
              <p className="text-center mt-3" style={{ color: '#6c757d' }}>
                Once submitted, someone from our team will review your application and contact you within 24–48 hours (সাবমিট করার পর, আমাদের দলের একজন আপনার আবেদন পর্যালোচনা করবেন এবং 24-48 ঘন্টার মধ্যে আপনার সাথে যোগাযোগ করবেন।).
              </p>

            </Form>
            
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default TrainingForm;